import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLScriptElement} [variantMedia] - JSON map of variant id → featured media id (option filter).
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-filter-by-tags')) {
      target?.addEventListener('change', this.#handleOptionChange, { signal });
      this.#preloadHiddenMedia();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  /**
   * Swaps option-tagged media ("#oval" in the alt text) as soon as an option is picked,
   * instead of waiting for the section re-render. The server applies the same rules
   * (snippets/media-option-hidden.liquid) and its gallery replaces this one afterwards.
   *
   * @param {Event} event - The change event from the variant picker.
   */
  #handleOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const picker = input.closest('variant-picker');
    if (!(picker instanceof HTMLElement)) return;

    // A combined-listing value loads another product; that gallery comes from the server.
    const connectedUrl = input.dataset.connectedProductUrl;
    if (connectedUrl && connectedUrl !== picker.dataset.productUrl?.split('?')[0]) return;

    this.#filterMedia(picker, input.dataset.variantId);
  };

  /**
   * Applies the same rules as snippets/media-option-hidden.liquid to the rendered media.
   *
   * @param {HTMLElement} picker - The variant picker holding the current selection.
   * @param {string} [variantId] - The variant the clicked value selects.
   */
  #filterMedia(picker, variantId) {
    const options = Array.from(picker.querySelectorAll('fieldset'))
      .map((fieldset) => {
        const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        const checked = inputs.find((radio) => radio instanceof HTMLInputElement && radio.checked);
        return {
          values: inputs.map((radio) => optionHandle(/** @type {HTMLInputElement} */ (radio))),
          selected: checked ? optionHandle(/** @type {HTMLInputElement} */ (checked)) : '',
        };
      })
      .filter((option) => option.values.length > 0);

    const variantMedia = this.#variantMedia;
    const variantMediaIds = new Set(Object.values(variantMedia));
    const selectedMediaId = variantId ? variantMedia[variantId] : undefined;

    const items = Array.from(this.querySelectorAll('[data-option-media-id]'));
    const isHidden = (/** @type {Element} */ item) => {
      const mediaId = item.getAttribute('data-option-media-id') || '';
      if (variantMediaIds.has(mediaId) && mediaId !== selectedMediaId) return true;

      const tags = item.getAttribute('data-media-tags') || '';
      if (!tags) return false;
      return options.some(
        (option) =>
          option.values.some((value) => tags.includes(`|${value}|`)) && !tags.includes(`|${option.selected}|`)
      );
    };

    const hiddenStates = items.map(isHidden);
    const showAll = hiddenStates.every(Boolean);
    items.forEach((item, index) => item.toggleAttribute('hidden', !showAll && hiddenStates[index]));

    // The server lists the selected variant's image first; match that until its render lands.
    if (selectedMediaId) {
      for (const item of items) {
        if (item.getAttribute('data-option-media-id') === selectedMediaId) item.parentElement?.prepend(item);
      }
    }

    this.slideshow?.select(0, undefined, { animate: false });
  }

  /** @returns {Record<string, string>} variant id → featured media id */
  get #variantMedia() {
    try {
      return JSON.parse(this.refs.variantMedia?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /**
   * Hidden lazy images never load. Tag-filtered images are few, so they are warmed up once
   * the page is idle; variant images can number in the dozens, so each one is fetched when
   * the shopper points at (or touches) the option value that would show it.
   */
  #preloadHiddenMedia() {
    const eager = (/** @type {ParentNode} */ scope) => {
      for (const image of scope.querySelectorAll('img[loading="lazy"]')) image.setAttribute('loading', 'eager');
    };

    const preload = () => {
      const variantMediaIds = new Set(Object.values(this.#variantMedia));
      for (const item of this.querySelectorAll('[data-option-media-id][hidden]')) {
        if (!variantMediaIds.has(item.getAttribute('data-option-media-id') || '')) eager(item);
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preload, { timeout: 3000 });
    } else {
      setTimeout(preload, 1500);
    }

    const target = this.closest('.shopify-section, dialog');
    const { signal } = this.#controller;
    const warmVariant = (/** @type {Event} */ event) => {
      const label = event.target instanceof Element ? event.target.closest('label') : null;
      const input = label?.querySelector('input[data-variant-id]');
      if (!(input instanceof HTMLInputElement) || !input.closest('variant-picker')) return;

      const mediaId = this.#variantMedia[input.dataset.variantId || ''];
      if (!mediaId) return;
      for (const item of this.querySelectorAll(`[data-option-media-id="${mediaId}"]`)) eager(item);
    };
    target?.addEventListener('pointerover', warmVariant, { signal });
    target?.addEventListener('pointerdown', warmVariant, { signal });
    target?.addEventListener('focusin', warmVariant, { signal });
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/**
 * The handle of an option value input, matching Liquid's `handleize` used for alt tags.
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function optionHandle(input) {
  return (
    input.dataset.optionHandle ||
    input.value
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
  );
}
